import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Trash2, LogOut, Plus, Pencil, X, RefreshCw, Upload, Music, Film, Star, Eye, ListPlus, Sparkles, Wand2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, mediaUrl, artUrl } from "@/lib/api";

const EMPTY_FORM = { title: "", description: "", platform: "youtube", url: "", duration: "", published: true };
const TABS = ["episodes", "media", "ai", "messages", "subscribers", "payments"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("episodes");
  const [episodes, setEpisodes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiDetails, setAiDetails] = useState("");
  const [aiResult, setAiResult] = useState(null);
  const [aiWriting, setAiWriting] = useState(false);
  const [artPrompt, setArtPrompt] = useState("");
  const [artworks, setArtworks] = useState([]);
  const [artGenerating, setArtGenerating] = useState(false);

  const loadEpisodes = () => api.get("/episodes/manage").then((r) => setEpisodes(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadMessages = () => api.get("/messages").then((r) => setMessages(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadSubscribers = () => api.get("/subscribers").then((r) => setSubscribers(r.data)).catch((e) => toast.error(formatApiError(e)));
  const [payments, setPayments] = useState([]);
  const loadPayments = () => api.get("/payments/manage").then((r) => setPayments(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadMedia = () => api.get("/media/manage").then((r) => setMediaItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  const loadArtworks = () => api.get("/artworks").then((r) => setArtworks(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    if (user) {
      loadEpisodes();
      loadMessages();
      loadSubscribers();
      loadPayments();
      loadMedia();
      loadArtworks();
    }
  }, [user]);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center font-mono text-xs uppercase tracking-widest text-gray-500" data-testid="admin-loading">
        Loading…
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/episodes/${editingId}`, form);
        toast.success("Episode updated.");
      } else {
        await api.post("/episodes", form);
        toast.success("Episode published.");
      }
      setForm(EMPTY_FORM);
      setEditingId(null);
      loadEpisodes();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (ep) => {
    setEditingId(ep.id);
    setForm({ title: ep.title, description: ep.description, platform: ep.platform, url: ep.url, duration: ep.duration || "", published: ep.published });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this episode permanently?")) return;
    try {
      await api.delete(`/episodes/${id}`);
      toast.success("Episode deleted.");
      loadEpisodes();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const generateShowNotes = async (e) => {
    e.preventDefault();
    if (!aiTopic.trim()) return;
    setAiWriting(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/ai/show-writer", { topic: aiTopic, details: aiDetails });
      setAiResult(data);
      toast.success("Draft copy ready.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAiWriting(false);
    }
  };

  const useInEpisodeForm = () => {
    if (!aiResult) return;
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      title: aiResult.title,
      description: aiResult.description + (aiResult.show_notes ? `\n\nShow Notes:\n${aiResult.show_notes}` : ""),
    });
    setTab("episodes");
    toast.success("Loaded into the episode form.");
  };

  const generateArt = async (e) => {
    e.preventDefault();
    if (!artPrompt.trim()) return;
    setArtGenerating(true);
    try {
      await api.post("/ai/cover-art", { prompt: artPrompt });
      toast.success("Cover art generated — it's live in the Gallery.");
      setArtPrompt("");
      loadArtworks();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setArtGenerating(false);
    }
  };

  const removeArtwork = async (id) => {
    if (!window.confirm("Delete this artwork?")) return;
    try {
      await api.delete(`/artworks/${id}`);
      loadArtworks();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadMedia = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
    const fd = new FormData();
    fd.append("file", uploadFile);
    fd.append("title", uploadTitle);
    fd.append("description", uploadDesc);
    setUploading(true);
    setUploadProgress(0);
    try {
      await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => setUploadProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 0),
      });
      toast.success("Imported as a draft. Publish it when you're ready.");
      setUploadFile(null);
      setUploadTitle("");
      setUploadDesc("");
      loadMedia();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const toggleMedia = async (m, field) => {
    try {
      await api.put(`/media/${m.id}`, { [field]: !m[field] });
      loadMedia();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeMedia = async (id) => {
    if (!window.confirm("Delete this file permanently?")) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success("Deleted.");
      loadMedia();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const addToEpisodes = async (m) => {
    try {
      await api.post("/episodes", {
        title: m.title,
        description: m.description,
        platform: m.kind === "video" ? "video" : "mp3",
        url: m.storage_path,
        duration: "",
        published: true,
      });
      toast.success("Added to Latest Episodes.");
      loadEpisodes();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const formatSize = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : "");

  const inputCls = "w-full bg-ink border border-white/15 px-4 py-3 text-sm focus:outline-none focus:border-blood";

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink px-4 sm:px-8 lg:px-16 py-8">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-3">
              <span className="w-3 h-3 bg-blood" />
              <h1 className="font-display font-extrabold uppercase tracking-widest text-xl">
                Zo <span className="text-blood">Pound</span> — Control Room
              </h1>
            </div>
            <p className="font-mono text-xs text-gray-500 mt-1" data-testid="admin-user-email">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link data-testid="admin-view-site-link" to="/" className="font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors">
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-white/20 hover:border-blood hover:text-blood px-4 py-2 font-mono text-xs uppercase tracking-widest transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </header>

        <nav className="flex gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`admin-tab-${t}`}
              onClick={() => setTab(t)}
              className={`font-display font-bold uppercase tracking-wider px-5 py-2 border transition-colors ${
                tab === t ? "bg-blood border-blood text-white" : "border-white/15 text-gray-400 hover:border-blood/50"
              }`}
            >
              {t === "ai" ? "AI Studio" : t}
            </button>
          ))}
        </nav>

        {tab === "episodes" && (
          <div className="grid lg:grid-cols-[380px_1fr] gap-8">
            <form onSubmit={submit} className="bg-elevate border border-white/10 p-6 grid gap-4 h-fit" data-testid="episode-form">
              <h2 className="font-display font-bold uppercase text-xl">
                {editingId ? "Edit Episode" : "New Episode"}
              </h2>
              <input data-testid="episode-title-input" required placeholder="Title" value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              <textarea data-testid="episode-description-input" rows={3} placeholder="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputCls} resize-none`} />
              <select data-testid="episode-platform-select" value={form.platform}
                onChange={(e) => setForm({ ...form, platform: e.target.value })} className={inputCls}>
                <option value="youtube">YouTube</option>
                <option value="spotify">Spotify</option>
                <option value="soundcloud">SoundCloud</option>
                <option value="mp3">Direct MP3 / Uploaded Audio</option>
                <option value="video">Uploaded Video (from Media tab)</option>
              </select>
              <input data-testid="episode-url-input" required placeholder="Episode URL (link from the platform)" value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} />
              <input data-testid="episode-duration-input" placeholder="Duration (e.g. 42:10)" value={form.duration}
                onChange={(e) => setForm({ ...form, duration: e.target.value })} className={inputCls} />
              <label className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest text-gray-400">
                <input data-testid="episode-published-checkbox" type="checkbox" checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-red-600 w-4 h-4" />
                Published
              </label>
              <div className="flex gap-3">
                <button data-testid="episode-save-button" type="submit" disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider px-4 py-3 transition-colors">
                  <Plus size={16} /> {saving ? "Saving…" : editingId ? "Update" : "Publish"}
                </button>
                {editingId && (
                  <button data-testid="episode-cancel-edit-button" type="button"
                    onClick={() => { setEditingId(null); setForm(EMPTY_FORM); }}
                    className="px-4 border border-white/20 hover:border-blood transition-colors" aria-label="Cancel edit">
                    <X size={16} />
                  </button>
                )}
              </div>
            </form>

            <div className="grid gap-4" data-testid="episodes-admin-list">
              {episodes.map((ep, i) => (
                <div key={ep.id} data-testid={`admin-episode-row-${i}`}
                  className="bg-surface border border-white/10 p-5 flex flex-wrap items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-blood">{ep.platform}</span>
                      {!ep.published && <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Draft</span>}
                    </div>
                    <p className="font-display font-bold uppercase text-lg truncate">{ep.title}</p>
                    <p className="font-mono text-xs text-gray-500 truncate">{ep.url}</p>
                  </div>
                  <div className="flex gap-2">
                    <button data-testid={`admin-episode-edit-${i}`} onClick={() => startEdit(ep)}
                      className="p-2 border border-white/15 hover:border-royal hover:text-blue-400 transition-colors" aria-label="Edit">
                      <Pencil size={16} />
                    </button>
                    <button data-testid={`admin-episode-delete-${i}`} onClick={() => remove(ep.id)}
                      className="p-2 border border-white/15 hover:border-blood hover:text-blood transition-colors" aria-label="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
              {episodes.length === 0 && (
                <p className="font-mono text-sm text-gray-500" data-testid="admin-episodes-empty">No episodes yet. Publish the first transmission.</p>
              )}
            </div>
          </div>
        )}

        {tab === "media" && (
          <div className="grid lg:grid-cols-[380px_1fr] gap-8">
            <form onSubmit={uploadMedia} className="bg-elevate border border-white/10 p-6 grid gap-4 h-fit" data-testid="media-upload-form">
              <h2 className="font-display font-bold uppercase text-xl">Import File</h2>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 leading-relaxed">
                MP3 / WAV / M4A / OGG audio · MP4 / MOV / WEBM video. Imports land as drafts — publish when ready.
              </p>
              <input data-testid="media-title-input" placeholder="Title (defaults to filename)" value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)} className={inputCls} />
              <textarea data-testid="media-description-input" rows={2} placeholder="Description (optional)" value={uploadDesc}
                onChange={(e) => setUploadDesc(e.target.value)} className={`${inputCls} resize-none`} />
              <input data-testid="media-file-input" type="file" accept="audio/*,video/*" required
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="font-mono text-xs text-gray-400 file:bg-blood file:text-white file:border-0 file:px-4 file:py-2 file:font-display file:font-bold file:uppercase file:tracking-wider file:mr-4 file:cursor-pointer" />
              {uploading && (
                <div className="w-full bg-ink h-2" data-testid="media-upload-progress">
                  <div className="bg-blood h-2 transition-all" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
              <button data-testid="media-upload-button" type="submit" disabled={uploading || !uploadFile}
                className="flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider px-4 py-3 transition-colors">
                <Upload size={16} /> {uploading ? `Uploading ${uploadProgress}%` : "Import"}
              </button>
            </form>

            <div className="grid gap-4" data-testid="media-admin-list">
              {mediaItems.map((m, i) => (
                <div key={m.id} data-testid={`admin-media-row-${i}`} className="bg-surface border border-white/10 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-[10px] uppercase tracking-widest text-blood flex items-center gap-1">
                          {m.kind === "video" ? <Film size={12} /> : <Music size={12} />} {m.kind}
                        </span>
                        {!m.published && <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Draft</span>}
                        {m.featured && <span className="font-mono text-[10px] uppercase tracking-widest text-blue-400">Featured</span>}
                      </div>
                      <p className="font-display font-bold uppercase text-lg truncate">{m.title}</p>
                      <p className="font-mono text-xs text-gray-500 truncate">{m.original_filename} · {formatSize(m.size)}</p>
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`admin-media-publish-${i}`} onClick={() => toggleMedia(m, "published")}
                        title={m.published ? "Unpublish" : "Publish"}
                        className={`p-2 border transition-colors ${m.published ? "border-blood text-blood" : "border-white/15 text-gray-400 hover:border-blood hover:text-blood"}`}>
                        <Eye size={16} />
                      </button>
                      <button data-testid={`admin-media-feature-${i}`} onClick={() => toggleMedia(m, "featured")}
                        title="Feature in gallery"
                        className={`p-2 border transition-colors ${m.featured ? "border-gold text-gold" : "border-white/15 text-gray-400 hover:border-gold hover:text-gold"}`}>
                        <Star size={16} />
                      </button>
                      <button data-testid={`admin-media-to-episodes-${i}`} onClick={() => addToEpisodes(m)}
                        title="Add to Latest Episodes"
                        className="p-2 border border-white/15 text-gray-400 hover:border-royal hover:text-blue-400 transition-colors">
                        <ListPlus size={16} />
                      </button>
                      <button data-testid={`admin-media-delete-${i}`} onClick={() => removeMedia(m.id)}
                        className="p-2 border border-white/15 text-gray-400 hover:border-blood hover:text-blood transition-colors" aria-label="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  {m.kind === "video" ? (
                    <video data-testid={`admin-media-preview-${i}`} src={mediaUrl(m.storage_path)} controls className="mt-4 w-full max-h-44 bg-black" />
                  ) : (
                    <audio data-testid={`admin-media-preview-${i}`} src={mediaUrl(m.storage_path)} controls className="mt-4 w-full" />
                  )}
                </div>
              ))}
              {mediaItems.length === 0 && (
                <p className="font-mono text-sm text-gray-500" data-testid="admin-media-empty">
                  Nothing imported yet. Drop your first track or video above.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "ai" && (
          <div className="grid lg:grid-cols-2 gap-8" data-testid="ai-studio">
            <div className="bg-elevate border border-white/10 p-6" data-testid="ai-show-writer">
              <h2 className="font-display font-bold uppercase text-xl flex items-center gap-2">
                <Wand2 size={18} className="text-blood" /> AI Show Writer
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                Gemini 3.1 Pro writes your episode copy
              </p>
              <form onSubmit={generateShowNotes} className="grid gap-4 mt-5">
                <input data-testid="ai-topic-input" required placeholder="Episode topic (e.g. Haitian Flag Day special)"
                  value={aiTopic} onChange={(e) => setAiTopic(e.target.value)} className={inputCls} />
                <textarea data-testid="ai-details-input" rows={3} placeholder="Details, talking points, guests (optional)"
                  value={aiDetails} onChange={(e) => setAiDetails(e.target.value)} className={`${inputCls} resize-none`} />
                <button data-testid="ai-generate-copy-button" type="submit" disabled={aiWriting}
                  className="flex items-center justify-center gap-2 bg-blood hover:bg-red-700 disabled:opacity-50 font-display font-bold uppercase tracking-wider px-4 py-3 transition-colors">
                  <Sparkles size={16} /> {aiWriting ? "Writing…" : "Generate Copy"}
                </button>
              </form>
              {aiResult && (
                <div className="mt-6 grid gap-4" data-testid="ai-writer-result">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-blood mb-1">Title</p>
                    <p className="font-display font-bold text-lg" data-testid="ai-result-title">{aiResult.title}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-blood mb-1">Description</p>
                    <p className="text-sm text-gray-300 leading-relaxed" data-testid="ai-result-description">{aiResult.description}</p>
                  </div>
                  {aiResult.show_notes && (
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-widest text-blood mb-1">Show Notes</p>
                      <pre className="text-xs text-gray-400 whitespace-pre-wrap font-mono bg-ink border border-white/10 p-4" data-testid="ai-result-notes">{aiResult.show_notes}</pre>
                    </div>
                  )}
                  <button data-testid="ai-use-in-episode-button" onClick={useInEpisodeForm}
                    className="border border-blood text-blood hover:bg-blood hover:text-white font-display font-bold uppercase tracking-wider px-4 py-3 transition-colors">
                    Use in Episode Form
                  </button>
                </div>
              )}
            </div>

            <div className="bg-elevate border border-white/10 p-6" data-testid="ai-cover-art">
              <h2 className="font-display font-bold uppercase text-xl flex items-center gap-2">
                <ImagePlus size={18} className="text-gold" /> Cover Art Generator
              </h2>
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                Gemini Nano Banana · every piece goes live in the public Gallery
              </p>
              <form onSubmit={generateArt} className="flex gap-3 mt-5">
                <input data-testid="ai-art-prompt-input" required placeholder="Describe the artwork…"
                  value={artPrompt} onChange={(e) => setArtPrompt(e.target.value)} className={inputCls} />
                <button data-testid="ai-generate-art-button" type="submit" disabled={artGenerating}
                  className="shrink-0 bg-gold text-ink hover:bg-yellow-600 disabled:opacity-50 font-display font-bold uppercase tracking-wider px-5 py-3 transition-colors">
                  {artGenerating ? "Painting…" : "Generate"}
                </button>
              </form>
              <div className="grid grid-cols-2 gap-4 mt-6" data-testid="ai-artwork-list">
                {artworks.map((a, i) => (
                  <div key={a.id} data-testid={`ai-artwork-${i}`} className="group relative border border-white/10 overflow-hidden">
                    <img src={artUrl(a.storage_path)} alt={a.prompt} className="w-full h-40 object-cover" loading="lazy" />
                    <div className="p-2 flex items-center justify-between gap-2">
                      <p className="font-mono text-[10px] text-gray-500 truncate">{a.prompt}</p>
                      <button data-testid={`ai-artwork-delete-${i}`} onClick={() => removeArtwork(a.id)}
                        className="text-gray-500 hover:text-blood transition-colors shrink-0" aria-label="Delete artwork">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
                {artworks.length === 0 && (
                  <p className="col-span-2 font-mono text-sm text-gray-500" data-testid="ai-artwork-empty">
                    No artwork yet. Generate your first cover.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "messages" && (
          <div className="grid gap-4" data-testid="admin-messages-list">
            <div className="flex justify-end">
              <button data-testid="messages-refresh-button" onClick={loadMessages}
                className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {messages.map((m, i) => (
              <div key={m.id} data-testid={`admin-message-row-${i}`} className="bg-surface border border-white/10 p-5">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <p className="font-display font-bold uppercase text-lg">{m.name}</p>
                  <p className="font-mono text-xs text-gray-500">{new Date(m.created_at).toLocaleString()}</p>
                </div>
                <a href={`mailto:${m.email}`} className="font-mono text-xs text-blood hover:underline">{m.email}</a>
                <p className="mt-3 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && (
              <p className="font-mono text-sm text-gray-500" data-testid="admin-messages-empty">No messages yet.</p>
            )}
          </div>
        )}

        {tab === "subscribers" && (
          <div className="grid gap-4" data-testid="admin-subscribers-list">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-widest text-gray-400" data-testid="admin-subscribers-count">
                {subscribers.length} on the transmission list
              </p>
              <button data-testid="subscribers-refresh-button" onClick={loadSubscribers}
                className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {subscribers.map((s, i) => (
              <div key={s.id} data-testid={`admin-subscriber-row-${i}`}
                className="bg-surface border border-white/10 px-5 py-4 flex flex-wrap items-center justify-between gap-2">
                <span className="font-mono text-sm flex items-center gap-3">{s.email}
                  {s.member && <span data-testid={`admin-subscriber-member-${i}`} className="font-mono text-[10px] uppercase tracking-widest bg-blood/20 text-blood border border-blood/40 px-2 py-0.5">Insider $1.99/mo</span>}
                </span>
                <span className="font-mono text-xs text-gray-500">{new Date(s.created_at).toLocaleDateString()}</span>
              </div>
            ))}
            {subscribers.length === 0 && (
              <p className="font-mono text-sm text-gray-500" data-testid="admin-subscribers-empty">No subscribers yet.</p>
            )}
          </div>
        )}

        {tab === "payments" && (
          <div className="grid gap-4" data-testid="admin-payments-list">
            <div className="flex items-center justify-between">
              <p className="font-mono text-xs uppercase tracking-widest text-gray-400" data-testid="admin-payments-summary">
                {payments.filter((p) => p.payment_status === "paid").length} paid · $
                {payments.filter((p) => p.payment_status === "paid").reduce((a, p) => a + (p.amount || 0), 0).toFixed(2)} collected
              </p>
              <button data-testid="payments-refresh-button" onClick={loadPayments}
                className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-blood transition-colors">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
            {payments.map((p, i) => (
              <div key={p.id} data-testid={`admin-payment-row-${i}`}
                className="bg-surface border border-white/10 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-display font-bold uppercase text-sm">
                    {p.kind === "membership" ? "Insider membership" : "Donation"} — ${Number(p.amount).toFixed(2)}
                    {p.kind === "membership" && <span className="text-gray-500 font-mono text-[10px] ml-1">/ MO</span>}
                  </p>
                  <p className="font-mono text-xs text-gray-500 truncate">{p.email || p.name || "anonymous"}{p.message ? ` — “${p.message}”` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${p.payment_status === "paid" ? "text-green-400 border-green-500/40 bg-green-500/10" : p.payment_status === "pending" ? "text-yellow-400 border-yellow-500/40" : "text-gray-400 border-white/15"}`}>
                    {p.payment_status}
                  </span>
                  <span className="font-mono text-xs text-gray-500">{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {payments.length === 0 && (
              <p className="font-mono text-sm text-gray-500" data-testid="admin-payments-empty">No payments yet. Hit Refresh.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
